<script setup lang="ts">
/**
 * Indicatore della sessione di firma nella barra in alto: compare solo mentre c'è una sessione
 * aperta, con il conto alla rovescia della scadenza e il pulsante «Chiudi sessione». Lo stato
 * viene da `GET /firma-remota/sessione` (e dall'apertura fatta dal pop-up OTP).
 */
import { onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'
import { useFirmaSessioneStore } from '../stores/firmaSessione'

const { t } = useI18n()
const auth = useAuthStore()
const sessione = useFirmaSessioneStore()
const pannello = ref<{ toggle: (e: Event) => void; hide: () => void } | null>(null)

onMounted(() => {
  if (auth.has('FIRMA_REMOTA_USE')) void sessione.carica()
})
// Dopo un cambio utente (passpartout) o un nuovo login lo stato si rilegge dal server.
watch(() => auth.me?.userId, () => {
  sessione.azzera()
  if (auth.has('FIRMA_REMOTA_USE')) void sessione.carica()
})

async function chiudi(): Promise<void> {
  pannello.value?.hide()
  await sessione.chiudi()
}
</script>

<template>
  <li v-if="auth.has('FIRMA_REMOTA_USE') && sessione.attiva">
    <a
      id="firma-sessione-indicatore"
      class="cursor-pointer firma-sessione"
      :class="{ 'firma-sessione--in-scadenza': sessione.secondiRimasti <= 30 }"
      :title="t('firmaRemota.sessione.titolo', 'Sessione di firma aperta')"
      :aria-label="t('firmaRemota.sessione.titolo', 'Sessione di firma aperta')"
      @click="pannello?.toggle($event)"
    >
      <i class="pi pi-verified" />
      <span class="firma-sessione-tempo">{{ sessione.restante }}</span>
    </a>
    <Popover ref="pannello">
      <div class="flex flex-col gap-3 p-1 firma-sessione-pannello">
        <div>
          <div class="font-semibold">
            {{ t('firmaRemota.sessione.titolo', 'Sessione di firma aperta') }}
          </div>
          <div class="text-sm text-muted-color">
            {{ t('firmaRemota.sessione.scade', { tempo: sessione.restante }, 'Puoi firmare senza nuovo OTP ancora per {tempo}.') }}
          </div>
        </div>
        <Button
          :label="t('firmaRemota.sessione.chiudi', 'Chiudi sessione')"
          icon="pi pi-lock"
          severity="secondary"
          size="small"
          @click="chiudi"
        />
      </div>
    </Popover>
  </li>
</template>

<style scoped>
.firma-sessione {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  width: auto !important;
  padding: 0 0.6rem;
}
.firma-sessione-tempo {
  font-variant-numeric: tabular-nums;
  font-size: 0.85rem;
  font-weight: 600;
}
.firma-sessione--in-scadenza {
  color: var(--p-orange-500);
}
.firma-sessione-pannello {
  min-width: 15rem;
}
</style>
